/**
 * 稼働機器一覧画面。
 *
 * - 「稼働中」「アーカイブ」タブで decommissioned フラグを切替表示
 * - 行クリックで詳細モーダル
 * - 譲渡 (transferSoldDevice) と アーカイブ/復元 ボタン
 */
import {
  Archive,
  ArchiveRestore,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Cpu,
  Repeat,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMailContacts } from '@/hooks/useMailContacts';
import {
  useArchiveSoldDevice,
  useSoldDevices,
  useTransferSoldDevice,
  useUnarchiveSoldDevice,
  type SoldDeviceRecord,
} from '@/hooks/useSalesOps';

import { ImportSoldDevicesDialog } from './dialogs/ImportSoldDevicesDialog';
import { SoldDeviceDetailDialog } from './dialogs/SoldDeviceDetailDialog';

type Tab = 'active' | 'archived';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

/** installedAt (ISO 文字列 or Firestore Timestamp) を比較用ミリ秒に変換 */
function toTime(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime() || 0;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().getTime();
  }
  return 0;
}

/** 検索用正規化: 小文字化 + 全半角統一 + 空白除去 */
function norm(s: string): string {
  return s.normalize('NFKC').toLowerCase().replace(/\s+/g, '');
}

interface SearchSuggestion {
  value: string;
  category: string;
}

export function SoldDevicesListPage() {
  const { data: devices, isLoading } = useSoldDevices();
  const { data: contacts } = useMailContacts();
  const transfer = useTransferSoldDevice();
  const archive = useArchiveSoldDevice();
  const unarchive = useUnarchiveSoldDevice();

  const [tab, setTab] = useState<Tab>('active');
  const [target, setTarget] = useState<SoldDeviceRecord | null>(null);
  const [detailTarget, setDetailTarget] = useState<SoldDeviceRecord | null>(null);
  const [toContactId, setToContactId] = useState('');
  const [reason, setReason] = useState('');
  const [transferredAt, setTransferredAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [importOpen, setImportOpen] = useState(false);

  // 検索 (チップ式・複合 AND) + 設置日ソート
  const [chips, setChips] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [sortAsc, setSortAsc] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const contactMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contacts ?? []) m.set(c.id, c.name || c.defaultEmail);
    return m;
  }, [contacts]);

  /** contactId → 検索対象テキスト (名前 / 全メアド / 会社名) */
  const contactSearchMap = useMemo(() => {
    const m = new Map<
      string,
      { name: string; emails: string[]; company: string }
    >();
    for (const c of contacts ?? []) {
      m.set(c.id, {
        name: c.name || '',
        emails: [
          c.defaultEmail,
          ...(c.emails ?? []).map((e) => e.address),
        ].filter(Boolean),
        company: c.companyName || '',
      });
    }
    return m;
  }, [contacts]);

  const activeCount = useMemo(
    () => (devices ?? []).filter((d) => !d.decommissioned).length,
    [devices],
  );
  const archivedCount = useMemo(
    () => (devices ?? []).filter((d) => d.decommissioned).length,
    [devices],
  );

  const visibleDevices = useMemo(() => {
    if (!devices) return [];
    return devices.filter((d) =>
      tab === 'active' ? !d.decommissioned : d.decommissioned,
    );
  }, [devices, tab]);

  /** 機器ごとの検索対象フィールド (正規化済み) */
  const deviceHaystacks = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const d of visibleDevices) {
      const fields: string[] = [d.serialNumber, d.modelName, d.productName];
      for (const cid of [d.contactId, d.ownerContactId]) {
        if (!cid) continue;
        const c = contactSearchMap.get(cid);
        if (!c) continue;
        fields.push(c.name, c.company, ...c.emails);
      }
      m.set(
        d.id,
        fields.filter(Boolean).map((f) => norm(f)),
      );
    }
    return m;
  }, [visibleDevices, contactSearchMap]);

  /** チップ + 入力中テキストの AND フィルタ → 設置日ソート */
  const filteredDevices = useMemo(() => {
    const terms = [...chips, searchInput.trim()]
      .filter(Boolean)
      .map((t) => norm(t));
    const filtered =
      terms.length === 0
        ? visibleDevices
        : visibleDevices.filter((d) => {
            const hay = deviceHaystacks.get(d.id) ?? [];
            return terms.every((t) => hay.some((f) => f.includes(t)));
          });
    return [...filtered].sort(
      (a, b) =>
        (toTime(a.installedAt) - toTime(b.installedAt)) * (sortAsc ? 1 : -1),
    );
  }, [visibleDevices, deviceHaystacks, chips, searchInput, sortAsc]);

  /** 入力中テキストに一致する候補 (カテゴリ付き・最大8件) */
  const suggestions = useMemo((): SearchSuggestion[] => {
    const q = norm(searchInput.trim());
    if (!q) return [];
    const out: SearchSuggestion[] = [];
    const seen = new Set<string>();
    const add = (value: string, category: string) => {
      if (!value || out.length >= 8) return;
      const key = norm(value);
      if (!key.includes(q) || seen.has(key)) return;
      if (chips.some((c) => norm(c) === key)) return;
      seen.add(key);
      out.push({ value, category });
    };
    for (const d of visibleDevices) {
      for (const cid of [d.contactId, d.ownerContactId]) {
        if (!cid) continue;
        const c = contactSearchMap.get(cid);
        if (!c) continue;
        add(c.name, '所有者');
        add(c.company, '会社名');
        for (const e of c.emails) add(e, 'メール');
      }
      add(d.serialNumber, 'S/N');
      add(d.modelName, 'モデル名');
      if (out.length >= 8) break;
    }
    return out;
  }, [searchInput, chips, visibleDevices, contactSearchMap]);

  const addChip = (value: string) => {
    const v = value.trim();
    if (!v) return;
    if (!chips.some((c) => norm(c) === norm(v))) setChips([...chips, v]);
    setSearchInput('');
    searchInputRef.current?.focus();
  };

  const removeChip = (idx: number) => {
    setChips(chips.filter((_, i) => i !== idx));
  };

  const handleTransfer = async () => {
    if (!target || !toContactId) return;
    try {
      await transfer.mutateAsync({
        soldDeviceId: target.id,
        toContactId,
        transferredAt,
        reason,
      });
      toast.success('譲渡を完了しました');
      setTarget(null);
      setToContactId('');
      setReason('');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleArchive = async (d: SoldDeviceRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`「${d.productName}」(S/N: ${d.serialNumber}) をアーカイブしますか？`)) {
      return;
    }
    try {
      await archive.mutateAsync(d.id);
      toast.success('アーカイブしました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleUnarchive = async (d: SoldDeviceRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await unarchive.mutateAsync(d.id);
      toast.success('アーカイブを解除しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title="稼働機器"
      description="納品完了で自動登録された機器の一覧。譲渡操作で所有者を更新できます。"
      actions={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setImportOpen(true)}
        >
          <Upload className="size-3.5" />
          インポート
        </Button>
      }
    >
      {/* タブ */}
      <div className="flex items-center gap-1 border-b border-[var(--border)] mb-3">
        {(
          [
            { key: 'active', label: '稼働中', count: activeCount },
            { key: 'archived', label: 'アーカイブ', count: archivedCount },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === t.key
                ? 'border-[var(--accent)] text-[var(--accent)] font-medium'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {t.label}
            <span className="ml-1.5 text-xs text-[var(--text-muted)]">
              ({t.count})
            </span>
          </button>
        ))}
      </div>

      {/* 検索 + 設置日ソート */}
      <div className="flex items-start gap-2 mb-3">
        <div className="relative flex-1">
          <div
            className="flex flex-wrap items-center gap-1.5 min-h-[38px] px-3 py-1.5 bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm cursor-text focus-within:border-[var(--accent)]"
            onClick={() => searchInputRef.current?.focus()}
          >
            <Search className="size-3.5 text-[var(--text-muted)] flex-shrink-0" />
            {chips.map((chip, i) => (
              <span
                key={`${chip}-${i}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[var(--bg-hover)] border border-[var(--border)] rounded-full"
              >
                {chip}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(i);
                  }}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  aria-label={`「${chip}」を削除`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <input
              ref={searchInputRef}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addChip(searchInput);
                } else if (
                  e.key === 'Backspace' &&
                  !searchInput &&
                  chips.length > 0
                ) {
                  removeChip(chips.length - 1);
                }
              }}
              placeholder={
                chips.length === 0
                  ? '所有者名 / メール / S/N / 会社名 / モデル名で検索…'
                  : ''
              }
              className="flex-1 min-w-[120px] text-sm bg-transparent outline-none placeholder:text-[var(--text-muted)]"
            />
          </div>
          {searchFocused && suggestions.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm shadow-lg overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={`${s.category}-${s.value}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addChip(s.value)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-muted)] flex-shrink-0 w-16 text-center">
                    {s.category}
                  </span>
                  <span className="truncate">{s.value}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setSortAsc((v) => !v)}
          title={sortAsc ? '設置日が古い順 (クリックで新しい順)' : '設置日が新しい順 (クリックで古い順)'}
          className="h-[38px] flex-shrink-0"
        >
          {sortAsc ? (
            <ArrowUpNarrowWide className="size-3.5" />
          ) : (
            <ArrowDownWideNarrow className="size-3.5" />
          )}
          設置日{sortAsc ? '古い順' : '新しい順'}
        </Button>
      </div>

      {(chips.length > 0 || searchInput.trim()) && !isLoading && (
        <p className="text-xs text-[var(--text-muted)] mb-2">
          {filteredDevices.length} 件が一致
        </p>
      )}

      {isLoading ? (
        <Spinner />
      ) : filteredDevices.length === 0 && visibleDevices.length > 0 ? (
        <EmptyState
          icon={Search}
          title="一致する機器がありません"
          description="検索キーワードを変更するか、チップを削除してください。"
        />
      ) : visibleDevices.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title={tab === 'active' ? '稼働機器はまだありません' : 'アーカイブはまだありません'}
          description={
            tab === 'active'
              ? '発送を納品完了にするとシリアル番号ごとに自動登録されます。'
              : '壊れた機器をアーカイブするとここに移動します。'
          }
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {filteredDevices.map((d) => {
            const transferCount = d.transferHistory?.length ?? 0;
            return (
              <div
                key={d.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailTarget(d)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetailTarget(d);
                  }
                }}
                className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              >
                <Cpu className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[13px] truncate">
                      {d.productName}
                      {d.variation && (
                        <span className="ml-1 text-xs text-[var(--text-muted)] font-normal">
                          ({d.variation})
                        </span>
                      )}
                    </span>
                    {transferCount > 0 && (
                      <Badge variant="review">譲渡 {transferCount} 回</Badge>
                    )}
                    {d.decommissioned && (
                      <Badge variant="default">アーカイブ済</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                    <span>{d.modelName}</span>
                    <code className="font-mono bg-[var(--bg-hover)] px-1.5 py-0.5 rounded text-[10px]">
                      S/N: {d.serialNumber}
                    </code>
                    {/* T4: 親 (所有者) があれば「使用者 / 所有者」を併記、無ければ「所有」のみ */}
                    {d.ownerContactId && d.ownerContactId !== d.contactId ? (
                      <>
                        <span>使用: {contactMap.get(d.contactId) ?? d.contactId}</span>
                        <span className="text-[var(--accent-purple)]">
                          所有 (親): {contactMap.get(d.ownerContactId) ?? d.ownerContactId}
                        </span>
                      </>
                    ) : (
                      <span>所有: {contactMap.get(d.contactId) ?? d.contactId}</span>
                    )}
                    <span>設置: {fmtDate(d.installedAt)}</span>
                  </div>
                </div>
                {tab === 'active' ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleArchive(d, e)}
                      title="アーカイブ (壊れた機器)"
                      disabled={archive.isPending}
                    >
                      <Archive />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTarget(d);
                      }}
                      title="譲渡"
                    >
                      <Repeat />
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleUnarchive(d, e)}
                    title="アーカイブ解除"
                    disabled={unarchive.isPending}
                  >
                    <ArchiveRestore />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>機器を譲渡</DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-3">
              <p className="text-sm">
                対象: {target.productName} ({target.serialNumber})
              </p>
              <label className="block text-xs">
                譲渡先
                <select
                  value={toContactId}
                  onChange={(e) => setToContactId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
                >
                  <option value="">選択してください</option>
                  {(contacts ?? [])
                    .filter((c) => c.id !== target.contactId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.defaultEmail}
                      </option>
                    ))}
                </select>
              </label>
              <label className="block text-xs">
                譲渡日
                <input
                  type="date"
                  value={transferredAt}
                  onChange={(e) => setTransferredAt(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
                />
              </label>
              <label className="block text-xs">
                譲渡理由
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setTarget(null)}>
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleTransfer}
              disabled={!toContactId || !reason.trim() || transfer.isPending}
            >
              譲渡確定
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SoldDeviceDetailDialog
        device={detailTarget}
        contactMap={contactMap}
        onOpenChange={(o) => !o && setDetailTarget(null)}
      />

      <ImportSoldDevicesDialog open={importOpen} onOpenChange={setImportOpen} />
    </Screen>
  );
}
